import { TZDate } from "@date-fns/tz";
import { addDays, isBefore, isToday, isWeekend, set } from "date-fns";
import type { ScheduleSendAt } from "domains/message-component/components/Compose/components/ScheduleSend/types/ScheduleSend.types";

import { START_OF_WORK_DAY } from "../constants";

const END_OF_WORK_DAY = 19;

/** Returns the date at 8 AM */
const getDateAt8Am = (date: Date): Date =>
    set(date, {
        hours: START_OF_WORK_DAY,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    });

/**
 * @returns the date of the working day following the date provided
 */
const getNextWorkingDaySchedule = (date: Date): Date => {
    const dayOfWeek = date.getDay();

    const getDaysToAdd = () => {
        switch (dayOfWeek) {
            // Friday, so we add three days to get to Monday
            case 5:
                return 3;
            // Saturday, so we add two days to get to Monday
            case 6:
                return 2;
            // Otherwise, we return 1 for the next day
            default:
                return 1;
        }
    };

    return getDateAt8Am(addDays(date, getDaysToAdd()));
};

/**
 * Determines the send time based on the current time.
 * - Returns "todayMorning" at 8 AM if the current time is before 8 AM on a working day
 * - Returns "nextWorkingDay" at 8 AM tomorrow if the time is after 7 PM or if today is a weekend
 * - Returns null if no conditions are met.
 */
export const getInitialScheduledSendTime = (): ScheduleSendAt => {
    const now = new TZDate(new Date(), "Europe/London");
    return getScheduledTimeInWorkingTime(now);
};

/** Returns the schedule for 8 AM on the provided date */
const getDateMorningSchedule = (sendAtDate: Date): ScheduleSendAt => ({
    type: isToday(sendAtDate) ? "todayMorning" : "calendar",
    sendAtDateTime: getDateAt8Am(sendAtDate).toISOString(),
});

/**
 * Calculates the send time based on the working hours.
 */
export const getScheduledTimeInWorkingTime = (
    sendAtDate: Date,
): ScheduleSendAt => {
    const sendAtHour = sendAtDate.getHours();
    const now = new TZDate(new Date(), "Europe/London");
    const currentHour = now.getHours();

    // if it's before now
    if (isBefore(sendAtDate, now)) {
        if (currentHour >= END_OF_WORK_DAY) {
            return {
                type: "nextWorkingDay",
                sendAtDateTime: getNextWorkingDaySchedule(now).toISOString(),
            };
        }
        return null;
    }

    const isWeekendOrAfterWork =
        isWeekend(sendAtDate) || sendAtHour >= END_OF_WORK_DAY;

    // if it's today instead of "calendar" return "todayMorning" or "nextWorkingDay" types
    if (isToday(sendAtDate)) {
        if (isWeekendOrAfterWork) {
            return {
                type: "nextWorkingDay",
                sendAtDateTime:
                    getNextWorkingDaySchedule(sendAtDate).toISOString(),
            };
        }
        if (sendAtHour < START_OF_WORK_DAY) {
            return getDateMorningSchedule(now);
        }
        return null;
    }

    // if it's after today, always return "calendar" type
    if (isWeekendOrAfterWork) {
        return getDateMorningSchedule(getNextWorkingDaySchedule(sendAtDate));
    }
    if (sendAtHour < START_OF_WORK_DAY) {
        return getDateMorningSchedule(sendAtDate);
    }

    return {
        type: "calendar",
        sendAtDateTime: sendAtDate.toISOString(),
    };
};
